'use client';

import { useState } from 'react';
import { m } from 'framer-motion';
import { useBoolean } from 'minimal-shared/hooks';

import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';

import { useRouter } from 'src/routes/hooks';
import { paths, newSearchParams } from 'src/routes/paths';

import { useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomTabs } from 'src/components/custom-tabs';
import { ButtonLoadMore } from 'src/components/custom-buttons';
import { useNotificationsContext } from 'src/components/notifications';
import { varTap, varHover, transitionTap } from 'src/components/animate';

import { NotificationItem } from './notification-item';

// ----------------------------------------------------------------------

export function NotificationsDrawer({ sx, onCloseAccountDrawer, ...other }) {
  const router = useRouter();

  const { t, currentLang } = useTranslate();

  const [currentTab, setCurrentTab] = useState('all');

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const {
    disabled,
    loadingMore,
    totalUnRead,
    allNotifications,
    handleMarkAsRead,
    totalNotifications,
    handleMarkAllAsRead,
    unreadNotifications,
    loadMoreNotifications,
  } = useNotificationsContext();

  const TABS = [
    { value: 'all', label: 'All', count: totalNotifications },
    { value: 'unread', label: 'Unread', count: totalUnRead },
  ];

  const handleOnClick = () => {
    onClose();
    onCloseAccountDrawer?.();
    router.push(
      `${paths.user.account}${newSearchParams({ country: currentLang.countryName, tab: 'notifications' })}`
    );
  };

  const renderHead = () => (
    <Box
      sx={{ py: 2, pl: 2.5, pr: 1.5, gap: 1, minHeight: 68, display: 'flex', alignItems: 'center' }}
    >
      <Box sx={{ flexGrow: 1, typography: 'h6' }}>{t('Notifications')}</Box>

      <Tooltip title={t(totalUnRead ? 'Mark all as read' : 'All read')}>
        <IconButton
          color={totalUnRead ? 'inherit' : 'info'}
          onClick={() => totalUnRead && handleMarkAllAsRead}
          sx={{ color: totalUnRead ? 'text.disabled' : 'info.main' }}
        >
          <Iconify icon="eva:done-all-fill" />
        </IconButton>
      </Tooltip>

      <IconButton onClick={handleOnClick}>
        <Iconify icon="solar:settings-bold-duotone" />
      </IconButton>

      <IconButton onClick={onClose}>
        <Iconify icon="mingcute:close-line" />
      </IconButton>
    </Box>
  );

  const renderTabs = () => (
    <CustomTabs
      value={currentTab}
      variant="fullWidth"
      onChange={(event, newValue) => setCurrentTab(newValue)}
    >
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          value={tab.value}
          iconPosition="end"
          label={t(tab.label)}
          icon={
            <Label
              variant={tab.value === 'all' || tab.value === currentTab ? 'filled' : 'soft'}
              color={tab.value === 'unread' ? 'info' : 'default'}
            >
              {tab.count}
            </Label>
          }
        />
      ))}
    </CustomTabs>
  );

  const renderList = () => (
    <Scrollbar>
      <Box component="ul">
        {allNotifications?.map((notification) => (
          <Box
            component="li"
            key={notification.id}
            sx={{ ...(currentTab === 'unread' && { height: 0, overflow: 'hidden' }) }}
          >
            <NotificationItem
              collection="all"
              notification={notification}
              handleMarkAsRead={handleMarkAsRead}
            />
          </Box>
        ))}
        {unreadNotifications?.map((notification) => (
          <Box
            component="li"
            key={notification.id}
            sx={{ ...(currentTab === 'all' && { height: 0, overflow: 'hidden' }) }}
          >
            <NotificationItem
              collection="unread"
              notification={notification}
              handleMarkAsRead={handleMarkAsRead}
            />
          </Box>
        ))}
      </Box>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        onClick={onOpen}
        disabled={disabled}
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label="Notifications button"
        sx={[...(Array.isArray(sx) ? sx : [sx])]}
        {...other}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <SwipeableDrawer
        open={open}
        anchor="right"
        onOpen={onOpen}
        disableDiscovery
        onClose={onClose}
        disableSwipeToOpen
        disableBackdropTransition
        slotProps={{ paper: { sx: { width: { xs: 1, md: 370 } } } }}
      >
        {renderHead()}
        {renderTabs()}
        {renderList()}

        <Box sx={{ p: 2 }}>
          <ButtonLoadMore
            perPage={10}
            loading={loadingMore}
            onClick={() => loadMoreNotifications(currentTab)}
            total={currentTab === 'all' ? totalNotifications : totalUnRead}
            index={currentTab === 'all' ? allNotifications.length : unreadNotifications.length}
          />
        </Box>
      </SwipeableDrawer>
    </>
  );
}
